import { BroadcastChannel } from 'broadcast-channel';
import { Query, QueryClient, QueryKey, QueryState } from '@tanstack/angular-query-experimental';
import { v4 as guid } from 'uuid';

/** Canale su cui viaggia lo stato delle query fra le schede del browser aperte sull'applicazione. */
export const QUERY_CACHE_SYNC_CHANNEL = 'query-cache-sync-ch';

/** Stato di una query annunciato alle altre schede: viaggia solo quando porta un dato. */
interface QueryStateMessage {
  kind: 'state';
  senderId: string;
  queryKey: QueryKey;
  queryHash: string;
  state: QueryState;
}

interface QueryRemovedMessage {
  kind: 'removed';
  senderId: string;
  queryHash: string;
}

type QueryCacheSyncMessage = QueryStateMessage | QueryRemovedMessage;

export interface QueryCacheSyncOptions {
  /** Nome del canale, che i test cambiano per isolare le istanze fra una prova e l'altra. */
  channelName?: string;
  /**
   * Chiamata quando l'annuncio non parte, tipicamente perché il dato in cache non è clonabile.
   * Senza gestore l'annuncio viene perso in silenzio: la scheda che non l'ha ricevuto continua a leggere il
   * proprio valore, e lo ricarica alla prima invalidazione.
   */
  onPublishError?: (error: unknown, queryHash: string) => void;
}

/**
 * Allinea la cache delle query fra le schede del browser aperte sull'applicazione: ogni scheda annuncia i propri
 * stati con dato e applica quelli che riceve. Due regole lo tengono innocuo, e sono nelle guardie qui sotto.
 *
 * Contratto e divisione dei compiti con l'invalidazione fra schede: `tanstack-cache-sync.md`.
 *
 * @param queryClient - Client di cui sincronizzare la cache.
 * @param options - Nome del canale e gestore degli annunci non partiti.
 * @returns Funzione che chiude il canale e smette di annunciare.
 */
export const broadcastQueryCacheState = (queryClient: QueryClient, options?: QueryCacheSyncOptions): (() => void) => {
  const senderId = guid();
  const channelName = options?.channelName ?? QUERY_CACHE_SYNC_CHANNEL;
  // Con `webWorkerSupport` il ripiego dei browser senza `BroadcastChannel` è IndexedDB e non localStorage, che
  // serializzando in JSON consegnerebbe vuote le `Map` e i `Set` della cache.
  const channel = new BroadcastChannel<QueryCacheSyncMessage>(channelName, { webWorkerSupport: true });
  const queryCache = queryClient.getQueryCache();
  // Sospende gli annunci mentre si applica un messaggio ricevuto, per non rimandarlo indietro.
  let applying = false;

  const publish = (message: QueryCacheSyncMessage): void => {
    channel.postMessage(message).catch((error: unknown) => {
      options?.onPublishError?.(error, message.queryHash);
    });
  };

  const unsubscribe = queryCache.subscribe((event) => {
    if (applying) {
      return;
    }
    // `QueryCacheNotifyEvent` tipizza la query con generici `any`: qui si rilegge sui tipi del contratto.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const query: Query = event.query;
    const { queryHash, queryKey, state, observers } = query;
    if (event.type === 'removed') {
      // Una query lasciata cadere dal garbage collector non ha più osservatori: non riguarda le altre schede.
      if (observers.length > 0) {
        publish({ kind: 'removed', senderId, queryHash });
      }
      return;
    }
    // Una scheda appena aperta monta le proprie query senza dato: annunciare quello stato svuoterebbe la cache
    // delle schede che stanno già lavorando.
    const carriesFreshData = event.type === 'updated' && event.action.type === 'success';
    const bornWithData = event.type === 'added' && state.status === 'success';
    if (!carriesFreshData && !bornWithData) {
      return;
    }
    if (state.data === undefined) {
      return;
    }
    publish({ kind: 'state', senderId, queryKey, queryHash, state });
  });

  const applyRemoval = (queryHash: string): void => {
    const query = queryCache.get(queryHash);
    if (query) {
      queryCache.remove(query);
    }
  };

  const applyState = (message: QueryStateMessage): void => {
    const query = queryCache.get(message.queryHash);
    if (!query) {
      queryCache.build(queryClient, { queryKey: message.queryKey, queryHash: message.queryHash }, message.state);
      return;
    }
    // Solo in avanti: nessun valore vecchio sovrascrive uno nuovo, e l'eco di uno stato appena applicato torna
    // col medesimo `dataUpdatedAt`, quindi si ferma qui.
    if (message.state.dataUpdatedAt > query.state.dataUpdatedAt) {
      query.setState(message.state);
    }
  };

  channel.onmessage = (message: QueryCacheSyncMessage): void => {
    if (message.senderId === senderId) {
      return;
    }
    applying = true;
    try {
      if (message.kind === 'removed') {
        applyRemoval(message.queryHash);
        return;
      }
      applyState(message);
    } finally {
      applying = false;
    }
  };

  return () => {
    unsubscribe();
    void channel.close();
  };
};
